// ═══════════════════════════════════════════════════════════════
//  Poyais One — Design Tokens (modernized)
//  Drop-in replacement for the old `const T` in each page.
//  Same keys as before → existing pages inherit the new look for free.
//  New keys (accent, radius, shadow, space, type…) power the refreshed UI.
// ═══════════════════════════════════════════════════════════════

// ── Color ──────────────────────────────────────────────────────
// Keys marked (compat) existed in the old palette; values are refreshed.
// Values are CSS custom properties (see index.css :root / [data-theme="dark"]) so every
// consumer of T automatically follows the light/dark toggle with zero per-file changes.
export const T = {
  // surfaces
  bg:        'var(--py-bg)',
  surface:   'var(--py-surface)',
  card:      'var(--py-card)',
  cardAlt:   'var(--py-card)',

  // hairline borders (softer + lighter than before)
  border:    'var(--py-border)',
  borderMid: 'var(--py-border-mid)',
  divider:   'var(--py-divider)',

  // text
  text:       'var(--py-text)',
  textMid:    'var(--py-text-mid)',
  textMidAlt: 'var(--py-text-mid-alt)',
  textDim:    'var(--py-text-dim)',
  textDimAlt: 'var(--py-text-dim-alt)',
  textFaint:  'var(--py-text-faint)',

  // brand + platform
  accent:    'var(--py-accent)', // Poyais gold, primary brand accent — same in both themes
  accentDeep:'var(--py-accent-deep)',
  accentInk: 'var(--py-accent-ink)', // text/icons on the (theme-invariant) accent/gold fill — white, like every primary button
  blue:      'var(--py-blue)',
  blueText:  'var(--py-blue-text)',
  purple:    'var(--py-purple)',
  gold:      'var(--py-accent)', // Google Ads / brand (alias of accent)

  // semantic — fill (bright) + text (AA-safe on the current theme)
  green:         'var(--py-green)',
  greenText:     'var(--py-green-text)',
  amber:         'var(--py-amber)',
  amberText:     'var(--py-amber-text)',
  amberDeepText: 'var(--py-amber-deep-text)',
  red:           'var(--py-red)',
  redText:       'var(--py-red-text)',
  purpleText:    'var(--py-purple-text)',
  orangeText:    'var(--py-orange-text)',

  // extra surfaces / neutrals
  white:        'var(--py-white)',
  subtleBg:     'var(--py-subtle-bg)',
  trackBg:      'var(--py-track-bg)',
  tableDivider: 'var(--py-table-divider)',
  borderAlt:    'var(--py-border-alt)',
  borderSoft:   'var(--py-border-soft)',
  pageBg:       'var(--py-page-bg)',
  ink:          'var(--py-ink)', // fixed dark ink — always dark pill + white text regardless of theme

  // tints (soft semantic backgrounds)
  redTint:      'var(--py-red-tint)',
  greenTint:    'var(--py-green-tint)',
  amberTint:    'var(--py-amber-tint)',
  amberTintAlt: 'var(--py-amber-tint-alt)',
  purpleTint:   'var(--py-purple-tint)',
  blueTint:     'var(--py-blue-tint)',
  orangeTint:   'var(--py-orange-tint)',
} as const;

// ── Typography ─────────────────────────────────────────────────
export const FONT = "'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const MONO = "'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif"; // (compat) — tek font talebi üzerine Manrope
export const SERIF = "'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif";

// Type ramp — use for consistent sizing across pages.
export const type = {
  pageTitle:  { fontSize: 'clamp(20px, 2.4vw, 24px)', fontWeight: 800, letterSpacing: '-0.025em' },
  pageTitleSerif: { fontFamily: SERIF, fontSize: 'clamp(30px, 3.4vw, 40px)', fontWeight: 400, letterSpacing: '-0.015em', lineHeight: 1.1 },
  sectionTitle:{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' },
  kpi:        { fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', fontFamily: MONO, fontVariantNumeric: 'tabular-nums' },
  stat:       { fontSize: 17, fontWeight: 800, fontFamily: MONO, fontVariantNumeric: 'tabular-nums' },
  body:       { fontSize: 13.5, fontWeight: 500, lineHeight: 1.6 },
  small:      { fontSize: 12, fontWeight: 600 },
  eyebrow:    { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const },
  label:      { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' as const },
} as const;

// ── Radius ─────────────────────────────────────────────────────
export const radius = { sm: 8, md: 11, lg: 14, xl: 16, xxl: 18, panel: 20, pill: 999 } as const;

// ── Elevation ──────────────────────────────────────────────────
export const shadow = {
  card:  'var(--py-shadow-card)',
  hover: 'var(--py-shadow-hover)',
  pop:   'var(--py-shadow-pop)',
  none:  'none',
} as const;

// ── Spacing (px) ───────────────────────────────────────────────
export const space = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 32, 8: 40 } as const;

// ── Breakpoints ────────────────────────────────────────────────
// mobile: chrome collapses (hamburger). narrow: hide date pill / account label.
export const bp = { narrow: 560, mobile: 820, tablet: 1024 } as const;

// ── Tinting helper ─────────────────────────────────────────────
// Soft tinted background from any brand/semantic color, e.g. tint(T.green, 12).
export const tint = (color: string, pct = 12) =>
  `color-mix(in srgb, ${color} ${pct}%, #fff)`;
export const shade = (color: string, pct = 20) =>
  `color-mix(in srgb, ${color} ${100 - pct}%, #000)`;
// Opacity variant of a color, e.g. alpha(T.accent, 25) ≈ old `${T.accent}40` hex-alpha suffix.
// Needed because T's values are CSS custom properties now — a literal hex suffix can't be
// appended to a var() reference the way it could to a raw hex string.
export const alpha = (color: string, pct = 20) =>
  `color-mix(in srgb, ${color} ${pct}%, transparent)`;

export type Theme = typeof T;
